import React, { useState, useEffect } from 'react';
import AddSupplierModal from './AddSupplierModal';
import MaterialTypesListModal from './MaterialTypesListModal';
import './SupplierManagementModal.css';

const SupplierManagementModal = ({ open, onClose, onSuppliersChange }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [showMaterialTypeModal, setShowMaterialTypeModal] = useState(false);
  const [selectedSupplierForMaterials, setSelectedSupplierForMaterials] = useState(null);

  // Fetch suppliers when modal opens
  useEffect(() => {
    if (open) {
      fetchSuppliers();
    }
  }, [open]);

  const fetchSuppliers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/suppliers`);
      if (!response.ok) {
        throw new Error(`Failed to fetch suppliers: ${response.statusText}`);
      }
      const data = await response.json();
      setSuppliers(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching suppliers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSupplierAdded = () => {
    fetchSuppliers(); // Refresh the list
    setShowAddModal(false);
    setEditingSupplier(null);
    if (onSuppliersChange) {
      onSuppliersChange(); // Notify parent to refresh its supplier list
    }
  };

  const handleEditSupplier = (supplier) => {
    setEditingSupplier(supplier);
    setShowAddModal(true);
  };

  const handleManageMaterialTypes = (supplier) => {
    setSelectedSupplierForMaterials(supplier);
    setShowMaterialTypeModal(true);
  };

  const handleMaterialTypesChanged = () => {
    fetchSuppliers(); // Refresh supplier list to update material counts
    if (onSuppliersChange) {
      onSuppliersChange();
    }
  };

  const handleDeleteSupplier = async (supplier) => {
    if (!window.confirm(`Are you sure you want to delete "${supplier.name}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/suppliers/${supplier.id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete supplier');
      }

      fetchSuppliers(); // Refresh the list
      if (onSuppliersChange) {
        onSuppliersChange(); // Notify parent to refresh its supplier list
      }
    } catch (err) {
      setError(err.message);
      console.error('Error deleting supplier:', err);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-content supplier-management-modal">
          <div className="modal-header">
            <h2>🏢 Supplier Management</h2>
            <button className="modal-close" onClick={onClose}>
              &times;
            </button>
          </div>

          <div className="modal-body">
            {/* Action Bar */}
            <div className="action-bar">
              <div className="suppliers-count">
                {suppliers.length} supplier{suppliers.length !== 1 ? 's' : ''} found
              </div>
              <button 
                className="btn-primary"
                onClick={() => setShowAddModal(true)}
              >
                ➕ Add New Supplier
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="error-message">
                <strong>Error:</strong> {error}
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading suppliers...</p>
              </div>
            ) : (
              /* Suppliers List */
              <div className="suppliers-list">
                {suppliers.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">🏢</div>
                    <h3>No Suppliers Found</h3>
                    <p>Get started by adding your first supplier.</p>
                    <button 
                      className="btn-primary"
                      onClick={() => setShowAddModal(true)}
                    >
                      ➕ Add First Supplier
                    </button>
                  </div>
                ) : (
                  suppliers.map(supplier => (
                    <div key={supplier.id} className="supplier-card">
                      <div className="supplier-info">
                        <div className="supplier-header">
                          <h3>{supplier.name}</h3>
                          <div className="supplier-stats">
                            {supplier._count?.materialTypes || 0} material type{(supplier._count?.materialTypes || 0) !== 1 ? 's' : ''}
                          </div>
                        </div>
                        
                        <div className="supplier-details">
                          {supplier.email && (
                            <div className="contact-item">
                              <span className="contact-icon">📧</span>
                              <span>{supplier.email}</span>
                            </div>
                          )}
                          {supplier.phone && (
                            <div className="contact-item">
                              <span className="contact-icon">📞</span>
                              <span>{supplier.phone}</span>
                            </div>
                          )}
                          {supplier.address && (
                            <div className="contact-item">
                              <span className="contact-icon">📍</span>
                              <span>{supplier.address}</span>
                            </div>
                          )}
                          {supplier.contactInfo && (
                            <div className="contact-item">
                              <span className="contact-icon">💬</span>
                              <span>{supplier.contactInfo}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="supplier-actions">
                        <button
                          className="btn-materials"
                          onClick={() => handleManageMaterialTypes(supplier)}
                          title="Manage material types"
                        >
                          📋 Materials
                        </button>
                        <button
                          className="btn-edit"
                          onClick={() => handleEditSupplier(supplier)}
                          title="Edit supplier"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDeleteSupplier(supplier)}
                          title="Delete supplier"
                          disabled={(supplier._count?.rawMaterials || 0) > 0}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Add/Edit Supplier Modal */}
      <AddSupplierModal
        open={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingSupplier(null);
        }}
        onSupplierAdded={handleSupplierAdded}
        editSupplier={editingSupplier}
      />

      {/* Material Types List Modal */}
      <MaterialTypesListModal
        open={showMaterialTypeModal}
        onClose={() => {
          setShowMaterialTypeModal(false);
          setSelectedSupplierForMaterials(null);
        }}
        supplier={selectedSupplierForMaterials}
        onMaterialTypesChange={handleMaterialTypesChanged}
      />
    </>
  );
};

export default SupplierManagementModal;
